/**
 * constants/blogDefaults.ts
 * Default configuration for blog post generation features
 */

// Define the blog features interface
export interface BlogFeatures {
  includeIntroduction: boolean;
  longerParagraphs: boolean;
  includeConclusion: boolean;
  includeKeyTakeaways: boolean;
}

// Text representations of feature states
export interface BlogFeaturePromptText {
  introductionText: string;
  paragraphsText: string;
  conclusionText: string;
  keyTakeawaysText: string;
}

// All features disabled by default
export const DEFAULT_BLOG_FEATURES: BlogFeatures = {
  // When false, skips the introduction section
  includeIntroduction: false,
  
  // When false, uses more concise paragraphs rather than longer ones
  longerParagraphs: false,
  
  // When false, skips the conclusion section
  includeConclusion: false,
  
  // When false, skips the key takeaways section
  includeKeyTakeaways: false
};

/**
 * Helper function to get default blog options with any provided overrides
 * @param overrides - Optional overrides for default values
 * @returns The merged options with defaults applied
 */
export const getDefaultBlogOptions = (overrides: Partial<BlogFeatures> = {}): BlogFeatures => {
  return {
    ...DEFAULT_BLOG_FEATURES,
    ...overrides
  };
};

/**
 * Get prompt text for each feature based on its current state
 * This ensures consistent messaging in prompts
 * @param options - The current feature options
 * @returns Text representations for each feature
 */
export const getBlogFeaturePromptText = (options: BlogFeatures): BlogFeaturePromptText => {
  return {
    introductionText: options.includeIntroduction 
      ? "An introduction (brief or extended)."
      : "NO introduction section.",
      
    paragraphsText: options.longerParagraphs
      ? "Use longer, in-depth paragraphs."
      : "Use concise, focused paragraphs.",
      
    conclusionText: options.includeConclusion
      ? "A conclusion at the end."
      : "NO conclusion section.",
      
    keyTakeawaysText: options.includeKeyTakeaways
      ? "A final 'Key Takeaways' section summarizing the main points."
      : "No separate key takeaways section."
  };
};